import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Check, Zap } from "lucide-react";

// Static, lightweight visual mock matching the dashboard's agent + decision cards.
// Not interactive — purely a hero showpiece.

const MOCK_AGENTS = [
  {
    id: "atlas",
    role: "CTO",
    name: "Atlas",
    status: "DEBATING",
    statusColor: "#7C3AED",
    bg: "bg-[#0B0B0E]",
    text: "text-white",
    quote:
      "Refactoring to serverless drops infra cost ~40%. Two-sprint migration if we feature-flag.",
  },
  {
    id: "ledger",
    role: "CFO",
    name: "Ledger",
    status: "THINKING",
    statusColor: "#2563EB",
    bg: "bg-gradient-to-b from-[#EAF1FF] to-white",
    text: "text-zinc-900",
    quote:
      "Runway 9.2 mo at $12k MRR growth. A 6-week contractor fits without touching reserves.",
  },
  {
    id: "aura",
    role: "CMO",
    name: "Aura",
    status: "IDLE",
    statusColor: "#9CA3AF",
    bg: "bg-gradient-to-b from-[#FAFAFA] to-white",
    text: "text-zinc-900",
    quote:
      "Waitlist warm — 1,400 signups primed. Re-activation drip ready in 24 hours.",
  },
  {
    id: "nexus",
    role: "COO",
    name: "Nexus",
    status: "DEBATING",
    statusColor: "#7C3AED",
    bg: "bg-gradient-to-b from-[#F2EBFF] to-white",
    text: "text-zinc-900",
    quote:
      "If we go enterprise, SOC2 Type I needs to start now. 45-day window minimum.",
  },
];

export default function HeroBoardroomMock() {
  const wrapRef = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const handle = (e) => {
      const r = el.getBoundingClientRect();
      const cx = (e.clientX - r.left) / r.width - 0.5;
      const cy = (e.clientY - r.top) / r.height - 0.5;
      setTilt({ x: -cy * 4, y: cx * 4 });
    };
    const reset = () => setTilt({ x: 0, y: 0 });
    el.addEventListener("mousemove", handle);
    el.addEventListener("mouseleave", reset);
    return () => {
      el.removeEventListener("mousemove", handle);
      el.removeEventListener("mouseleave", reset);
    };
  }, []);

  return (
    <div
      ref={wrapRef}
      data-testid="hero-boardroom-mock"
      className="relative mx-auto"
      style={{ perspective: 1400 }}
    >
      <motion.div
        animate={{ y: [0, -6, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        style={{
          transform: `rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
          transformStyle: "preserve-3d",
          transition: "transform 0.18s ease-out",
        }}
        className="mx-auto max-w-5xl rounded-[28px] border border-black/[0.05] bg-white/55 p-4 shadow-[0_40px_80px_-40px_rgba(15,15,25,0.35)] backdrop-blur-2xl md:p-6"
      >
        {/* Inner header strip */}
        <div className="mb-4 flex items-center justify-between rounded-2xl border border-black/[0.04] bg-white/80 px-4 py-2.5">
          <div className="flex items-center gap-2">
            <div className="text-[12px] font-extrabold tracking-tight">
              Lumen AI
            </div>
            <span className="rounded-full border border-black/[0.06] bg-white px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.18em] text-zinc-700">
              Pre-seed
            </span>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] text-violet-700">
            <span className="h-1.5 w-1.5 rounded-full bg-violet-600 idea-dot-pulse" />
            Live
          </span>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          {/* 4 agent mini cards in left 2/3 */}
          <div className="grid grid-cols-2 gap-3 md:col-span-2">
            {MOCK_AGENTS.map((a) => (
              <div
                key={a.id}
                data-testid={`hero-mock-agent-${a.id}`}
                className={`relative aspect-[1/1.12] overflow-hidden rounded-2xl border border-black/[0.04] p-4 ${a.bg} ${a.text}`}
              >
                <div className="flex items-start justify-between">
                  <div
                    className={`text-[9px] font-bold uppercase tracking-[0.18em] ${a.id === "atlas" ? "text-white/55" : "text-zinc-500"}`}
                  >
                    {a.role}
                  </div>
                  <span
                    className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.14em] ${a.id === "atlas" ? "bg-white/10 text-white/85" : "bg-zinc-900/[0.04] text-zinc-700"}`}
                  >
                    <span
                      className={`h-1.5 w-1.5 rounded-full ${a.status !== "IDLE" ? "idea-dot-pulse" : ""}`}
                      style={{ backgroundColor: a.statusColor }}
                    />
                    {a.status}
                  </span>
                </div>
                <div className="mt-2 text-[20px] font-extrabold leading-none tracking-tight">
                  {a.name}
                </div>
                <p
                  className={`mt-2 line-clamp-3 text-[10.5px] leading-snug ${a.id === "atlas" ? "text-white/80" : "text-zinc-600"}`}
                >
                  {a.quote}
                </p>
              </div>
            ))}
          </div>

          {/* Decision summary mini */}
          <div
            data-testid="hero-mock-decision"
            className="relative flex flex-col overflow-hidden rounded-2xl border border-black/[0.04] bg-white/85 p-4 backdrop-blur"
          >
            <div className="flex items-center justify-between">
              <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-zinc-500">
                Decision summary
              </div>
              <span className="inline-flex items-center gap-1 rounded-full bg-black px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.14em] text-white">
                <Zap className="h-2.5 w-2.5" strokeWidth={2.6} />
                Live
              </span>
            </div>
            <div className="mt-3 text-[14px] font-extrabold leading-tight tracking-tight text-zinc-900">
              Pivot to Enterprise SaaS
            </div>
            <div className="mt-3 flex items-baseline justify-between">
              <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-zinc-500">
                Confidence
              </div>
              <div className="text-[26px] font-extrabold leading-none tracking-tight text-zinc-900">
                86<span className="text-[12px] text-zinc-400">%</span>
              </div>
            </div>
            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-zinc-100">
              <div
                className="h-full rounded-full"
                style={{
                  width: "86%",
                  background:
                    "linear-gradient(90deg, #0a0a0a 0%, #6D28D9 55%, #22D3EE 100%)",
                }}
              />
            </div>
            <div className="mt-3 space-y-1.5">
              {[
                "Ship guided onboarding v1",
                "Start SOC2 Type I in parallel",
                "Hold off on bridge round",
              ].map((s, i) => (
                <div
                  key={i}
                  className="flex items-start gap-1.5 text-[10.5px] leading-snug text-zinc-700"
                >
                  <span className="mt-[2px] inline-flex h-3 w-3 flex-shrink-0 items-center justify-center rounded-full bg-zinc-900 text-white">
                    <Check className="h-2 w-2" strokeWidth={3} />
                  </span>
                  <span>{s}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
